import { inject, Injectable } from "@angular/core";
import { catchError, from, map, Observable, of, switchMap } from "rxjs";
import {
  CMS_ROUTE_PREFIX,
  CoreMediaNavigationClient,
} from "./coremedia-navigation.client";
import { environment } from "../../../../../environments/environment";
import { API_HEADERS } from "../../../../shared/api-headers";
import { ApiClient } from "../../../../shared/data/api.client";
import { userContextStore } from "../../../../shared/data/user-context.store";
import {
  NavigationChannelConfig,
  resolveNavigationChannel,
} from "../../../../shared/util/navigation-channel-config";
import { computeSiteHash } from "../../../../shared/util/site-hash";

export interface ResolvedLink {
  resolved: boolean;
  type?: string;
  route?: string;
  parentRoute?: string;
  anchor?: string;
  reason?: string;
}

interface LinkResolveApiResponse {
  data?: {
    links: Record<string, ResolvedLink>;
  };
  links?: Record<string, ResolvedLink>;
}

const CMS_V1 = environment.api.services.commons.coremediaV1;

@Injectable({ providedIn: "root" })
export class LinkResolverService {
  private readonly navClient = inject(CoreMediaNavigationClient);
  private readonly api = inject(ApiClient);
  private readonly userContext = inject(userContextStore);

  resolveLinks(ids: number[]): Observable<Map<number, ResolvedLink>> {
    return this.navClient.waitForAdditionalTrees().pipe(
      switchMap(() => {
        const resolved = new Map<number, ResolvedLink>();
        const unresolved: number[] = [];

        for (const id of ids) {
          const route = this.navClient.resolveRouteById(id);
          if (route) {
            resolved.set(id, { resolved: true, type: "CMChannel", route });
          } else {
            unresolved.push(id);
          }
        }

        if (unresolved.length === 0) {
          return of(resolved);
        }

        return this.resolveBatch(unresolved).pipe(
          map((backendResults) => {
            for (const [id, link] of backendResults) {
              resolved.set(id, link);
            }
            return resolved;
          }),
        );
      }),
    );
  }

  private resolveBatch(ids: number[]): Observable<Map<number, ResolvedLink>> {
    const channel = resolveNavigationChannel(
      this.userContext.country(),
      this.userContext.language(),
    );
    if (!channel) {
      return of(this.unresolvedMap(ids, "no_navigation_channel"));
    }

    return from(this.buildRequestBody(ids, channel)).pipe(
      switchMap((body) =>
        from(computeSiteHash(channel.siteName)).pipe(
          switchMap((siteHash) =>
            this.api
              .post<LinkResolveApiResponse>(
                `${CMS_V1}/navigation/resolveLinks`,
                body,
                { headers: { [API_HEADERS.siteHash]: siteHash } },
              )
              .pipe(
                map((response) => {
                  const links = response.data?.links ?? response.links ?? {};
                  const result = new Map<number, ResolvedLink>();
                  for (const [key, link] of Object.entries(links)) {
                    result.set(parseInt(key, 10), link);
                  }
                  return result;
                }),
              ),
          ),
        ),
      ),
      catchError((error) => {
        console.error("[LinkResolver] backend resolve failed", error);
        return of(this.unresolvedMap(ids, "backend_error"));
      }),
    );
  }

  private async buildRequestBody(
    ids: number[],
    channel: NavigationChannelConfig,
  ): Promise<{
    ids: string[];
    navigationRootId: string;
    primaryRoutePrefix: string;
    additionalTrees: {
      channelId: string;
      siteHash: string;
      routePrefix: string;
    }[];
    globalTrees: { channelId: string; siteHash: string; routePrefix: string }[];
  }> {
    const [additionalTrees, globalTrees] = await Promise.all([
      this.mapTreesWithHash(environment.navigation.additionalTrees),
      this.mapTreesWithHash(environment.navigation.globalTrees),
    ]);
    return {
      ids: ids.map(String),
      navigationRootId: channel.channelId,
      primaryRoutePrefix: CMS_ROUTE_PREFIX,
      additionalTrees,
      globalTrees,
    };
  }

  private async mapTreesWithHash(
    trees: NavigationChannelConfig[] | undefined,
  ): Promise<{ channelId: string; siteHash: string; routePrefix: string }[]> {
    const filtered = (trees ?? []).filter((t) => t.channelId);
    return Promise.all(
      filtered.map(async (t) => ({
        channelId: t.channelId,
        siteHash: await computeSiteHash(t.siteName),
        routePrefix: t.routePrefix ?? "",
      })),
    );
  }

  private unresolvedMap(
    ids: number[],
    reason: string,
  ): Map<number, ResolvedLink> {
    const result = new Map<number, ResolvedLink>();
    for (const id of ids) {
      result.set(id, { resolved: false, reason });
    }
    return result;
  }
}
